import React from "react";

/**
 * AdminApiTable — minimal generic table for read-only admin lists.
 *
 * Drives Sub-sprint A (Shops, Tickets). Mutation actions are NOT
 * surfaced; each row is rendered as plain data. Click handler is
 * optional and never triggers a network call by itself — it's a
 * navigation primitive (e.g. open detail page).
 *
 * Columns shape:
 *   [{ key, label, render?: (row) => ReactNode, className? }]
 */
export const AdminApiTable = ({
  columns,
  rows,
  loading,
  error,
  emptyLabel = "No rows",
  onRowClick,
  dataTestId = "rl-admin-api-table",
  rowTestIdKey = "id",
}) => {
  return (
    <div
      data-testid={dataTestId}
      className="overflow-x-auto rounded-md border border-slate-200 bg-white"
    >
      <table className="min-w-full text-sm">
        <thead className="bg-slate-50 text-slate-600">
          <tr>
            {columns.map((c) => (
              <th
                key={c.key}
                className={`px-3 py-2 text-left font-medium ${c.className || ""}`}
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {loading && (
            <tr>
              <td
                colSpan={columns.length}
                data-testid={`${dataTestId}-loading`}
                className="px-3 py-6 text-center text-slate-500"
              >
                Loading…
              </td>
            </tr>
          )}
          {!loading && error && (
            <tr>
              <td
                colSpan={columns.length}
                data-testid={`${dataTestId}-error`}
                className="px-3 py-6 text-center text-rose-700"
              >
                Error: {error.status || ""} {error.detail || "unknown"}
              </td>
            </tr>
          )}
          {!loading && !error && (!rows || rows.length === 0) && (
            <tr>
              <td
                colSpan={columns.length}
                data-testid={`${dataTestId}-empty`}
                className="px-3 py-6 text-center text-slate-500"
              >
                {emptyLabel}
              </td>
            </tr>
          )}
          {!loading &&
            !error &&
            rows &&
            rows.map((row, i) => (
              <tr
                key={row[rowTestIdKey] || i}
                data-testid={`${dataTestId}-row-${row[rowTestIdKey] || i}`}
                className={onRowClick ? "cursor-pointer hover:bg-slate-50" : ""}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
              >
                {columns.map((c) => (
                  <td key={c.key} className={`px-3 py-2 ${c.className || ""}`}>
                    {c.render ? c.render(row) : row[c.key] ?? "—"}
                  </td>
                ))}
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
};

export default AdminApiTable;
